import React, { useMemo } from 'react'
import { View, ScrollView, Platform } from 'react-native'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'
import LocationsSelectInput from '../../LocationsSelectInput'
import EquipmentsSelectInput from '../../EquipmentsSelectInput'
import Button from '../../Button'
import KeyboardView from '../../KeyboardView'
import { usePlatforms } from '../../../hooks/usePlatforms'
import { preferredLaneSelector } from '../../../redux/preferredLane/preferredLaneSelector'
import useStyles from './useStyles'

const emptyLane = {
  origin: null,
  destination: null,
  equipmentType: null
}

const validationSchema = Yup.object({
  origin: Yup.object()
    .nullable()
    .required('Origin is required'),
  destination: Yup.object()
    .nullable()
    .required('Destination is required')
})

const LaneForm = ({ lane, onSave, onClose }) => {
  const { isTablet } = usePlatforms()
  const styles = useStyles(isTablet)

  const { addLoading, editLoading } = useSelector(preferredLaneSelector)

  const setHandleSubmit = (handleSubmit, setFieldError, values) => {
    if (values.origin && values.destination && JSON.stringify(values.origin) === JSON.stringify(values.destination)) {
      return setFieldError('destination', 'Origin and destination can not be the same')
    }
    handleSubmit()
  }

  const smallDevice = useMemo(() => Platform.OS !== 'web' || !isTablet, [isTablet])

  return (
    <KeyboardView>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <Formik
          initialValues={lane?.lane_id ? lane : emptyLane}
          validationSchema={validationSchema}
          onSubmit={onSave}
        >
          {({ values, errors, handleSubmit, touched, setFieldValue, setFieldError }) => {
            return (
              <View
                style={[
                  styles.content,
                  {
                    maxWidth: isTablet ? 800 : 'auto'
                  }
                ]}
              >
                <View style={styles.directionContainer}>
                  <View
                    style={[
                      styles.contentContainer,
                      {
                        marginRight: isTablet ? 5 : 0
                      }
                    ]}
                  >
                    <LocationsSelectInput
                      name='origin'
                      placeholder='Origin'
                      selected={values?.origin || ''}
                      onChange={setFieldValue}
                      error={touched.origin && errors.origin}
                      origin
                      testID='preferredLaneOrigin'
                      accessibilityLabel='preferredLaneOrigin'
                    />
                  </View>
                  <View
                    style={[
                      styles.contentContainer,
                      {
                        marginLeft: isTablet ? 5 : 0
                      }
                    ]}
                  >
                    <LocationsSelectInput
                      name='destination'
                      placeholder='Destination'
                      selected={values?.destination || ''}
                      onChange={setFieldValue}
                      error={touched.destination && errors.destination}
                      destination
                      testID='preferredLaneDestination'
                      accessibilityLabel='preferredLaneDestination'
                    />
                  </View>
                </View>
                <View style={styles.directionContainer}>
                  <View style={styles.contentContainer}>
                    <EquipmentsSelectInput
                      name='equipmentType'
                      selected={values?.equipmentType || ''}
                      onChange={setFieldValue}
                      testID='preferredLaneEquipmentType'
                      accessibilityLabel='preferredLaneEquipmentType'
                    />
                  </View>
                </View>
                <View style={styles.buttonContainer}>
                  {!smallDevice && (
                    <Button
                      title='Cancel'
                      onPress={onClose}
                      containerStyle={styles.button}
                    />
                  )}
                  <Button
                    title={values?.lane_id ? 'Save' : 'Add' || ''}
                    onPress={() => setHandleSubmit(handleSubmit, setFieldError, values)}
                    loading={addLoading || editLoading}
                    testID='laneModalFormButton'
                    accessibilityLabel='laneModalFormButton'
                    containerStyle={styles.button}
                  />
                </View>
              </View>
            )
          }}
        </Formik>
      </ScrollView>
    </KeyboardView>
  )
}

LaneForm.propTypes = {
  lane: PropTypes.object,
  onSave: PropTypes.func,
  onClose: PropTypes.func
}

LaneForm.defaultProps = {
  lane: {},
  onSave: () => {},
  onClose: () => {}
}

export default LaneForm
